
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HeadingBlockConfiguration } from "@/types/block-configurations";
import {
  TypographyControls,
  AlignmentControls,
  PaddingControls,
  LinkControls,
  ResponsiveControls,
} from "../shared";

interface HeadingBlockPropertiesProps {
  config: HeadingBlockConfiguration;
  onChange: (config: HeadingBlockConfiguration) => void;
}

/**
 * HeadingBlockProperties Component
 * 
 * Property panel for heading blocks with text styling and level controls.
 * 
 * Sections:
 * - Content (always visible) - Heading text input and level selector
 * - Typography - Font, size, weight, line height, color
 * - Alignment - Horizontal and vertical alignment
 * - Spacing - Padding controls
 * - Text Style - Text decoration, transform, letter spacing
 * - Link - Optional link configuration
 * - Responsive - Mobile/desktop visibility
 * 
 * @param config - Current heading block configuration
 * @param onChange - Callback when configuration changes
 */
export function HeadingBlockProperties({ config, onChange }: HeadingBlockPropertiesProps) {
  const updateField = <K extends keyof HeadingBlockConfiguration>(
    field: K,
    value: HeadingBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div className="space-y-3">
      {/* Always Visible: Content Section */}
      <div className="space-y-3 p-3 rounded-md border border-border bg-card">
        <div>
          <Label htmlFor="heading-text">Heading Text</Label>
          <Input
            id="heading-text"
            type="text"
            value={config.text}
            onChange={(e) => updateField('text', e.target.value)}
            placeholder="Enter heading text..."
            maxLength={200}
            className="mt-1.5"
          />
          <p className="text-xs text-muted-foreground mt-1">
            {config.text.length}/200 characters
          </p>
        </div>

        <div>
          <Label htmlFor="heading-level">Heading Level</Label>
          <Select
            id="heading-level"
            value={config.level}
            onChange={(e) => updateField('level', e.target.value as HeadingBlockConfiguration['level'])}
            className="mt-1.5"
          >
            <option value="h1">H1 - Main Title</option>
            <option value="h2">H2 - Section Title</option>
            <option value="h3">H3 - Subsection</option>
            <option value="h4">H4 - Minor Heading</option>
            <option value="h5">H5 - Small Heading</option>
            <option value="h6">H6 - Smallest</option>
          </Select>
        </div>
      </div>

      {/* Collapsible Sections */}
      <Accordion type="multiple" defaultValue={["typography", "alignment"]} className="w-full">
        
        {/* Typography Section */}
        <AccordionItem value="typography">
          <AccordionTrigger>Typography</AccordionTrigger>
          <AccordionContent>
            <TypographyControls
              value={config.typography}
              onChange={(typography) => updateField('typography', typography)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Alignment Section */}
        <AccordionItem value="alignment">
          <AccordionTrigger>Alignment</AccordionTrigger>
          <AccordionContent>
            <AlignmentControls
              value={config.alignment}
              onChange={(alignment) => updateField('alignment', alignment)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Spacing Section */}
        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Text Style Section */}
        {/* Text Style Section -- italic/underline/strikethrough/textTransform/letterSpacing now live inside TypographyControls */}

        {/* Link Section */}
        <AccordionItem value="link">
          <AccordionTrigger>Link</AccordionTrigger>
          <AccordionContent>
            <LinkControls
              value={config.link}
              onChange={(link) => updateField('link', link)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Responsive Section */}
        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
